import {
    BleClient,
    BleDevice,
    numbersToDataView,
} from '@capacitor-community/bluetooth-le';

import { ESPConstants } from '../ESPConstants';
import { Transport } from './Transport';

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

/**
 * Bluetooth implementation of the Transport protocol.
 */
export class BLETransport implements Transport {
    private currentDevice?: BleDevice;
    private serviceUuid?: string;
    private uuidMap: { [key: string]: string } = {};

    /**
     * BLE implementation of Transport protocol
     *
     * @param path path of the config endpoint.
     * @param data config data to be sent
     *
     * @return Returns a Promise with an Uint8Array
     */
    public async sendConfigData(
        path: string,
        data: Uint8Array
    ): Promise<Uint8Array> {
        if (!this.uuidMap[path]) {
            throw new Error('Characteristic is not available for given path.');
        }

        const characteristic = this.uuidMap[path];

        await BleClient.write(
            this.currentDevice!.deviceId,
            this.serviceUuid!,
            characteristic,
            numbersToDataView(Array.from(data)),
            {
                timeout: 10000,
            }
        );

        const result = await BleClient.read(
            this.currentDevice!.deviceId,
            this.serviceUuid!,
            characteristic,
            {
                timeout: 10000,
            }
        );
        const deviceResponse = new Uint8Array(result.buffer);

        return deviceResponse;
    }

    /**
     * Connect to a BLE peripheral device.
     *
     * @param bluetoothDevice    The peripheral device
     * @param primaryServiceUuid Primary Service UUID
     */
    public async connect(
        bluetoothDevice: BleDevice,
        primaryServiceUuid?: string
    ): Promise<void> {
        await BleClient.disconnect(bluetoothDevice.deviceId);
        await BleClient.connect(bluetoothDevice.deviceId, (deviceId) => {
            console.log(`Device ${bluetoothDevice.name} is disconnected`);
        });

        // Try to bond with the device (Android only)
        try {
            const isBonded = await BleClient.isBonded(
                bluetoothDevice.deviceId
            );
            if (!isBonded) {
                await BleClient.createBond(bluetoothDevice.deviceId);
            }
        } catch (e) {
            console.log('Error while bonding with the device', e);
        }

        const serviceUuid = primaryServiceUuid || ESPConstants.SERVICE_UUID;
        const services = await BleClient.getServices(bluetoothDevice.deviceId);
        const service = services.find(
            (service) => service.uuid === serviceUuid
        );

        if (service) {
            await Promise.all(
                service.characteristics.map(async (characteristic) => {
                    const descriptor = await BleClient.readDescriptor(
                        bluetoothDevice.deviceId,
                        serviceUuid,
                        characteristic.uuid,
                        characteristic.descriptors[0].uuid
                    );
                    const value = textDecoder.decode(
                        new Uint8Array(descriptor.buffer)
                    );
                    this.uuidMap[value] = characteristic.uuid;
                })
            );
        } else {
            // Fallback to the characteristic UUIDs
            this.uuidMap = ESPConstants.CHARACTERISTICS;
        }

        // TODO: Check this later. Previous code was not working in iOS
        // this.uuidMap = ESPConstants.CHARACTERISTICS;

        this.currentDevice = bluetoothDevice;
        this.serviceUuid = serviceUuid;

        await this.sendConfigData(
            ESPConstants.HANDLER_PROTO_VER,
            textEncoder.encode('ESP')
        );
    }

    /**
     * Disconnect from the current connected peripheral
     */
    public disconnect(): Promise<void> {
        if (!this.currentDevice) {
            return Promise.resolve();
        }
        return BleClient.disconnect(this.currentDevice!.deviceId);
    }
}
